import { ProductionUnit, ProjectArrays, ContractType, ValidationRangeTypes } from '~/constants';
import {
  areRecStateFieldsEnabled,
  nonEmptyString,
  range,
  validationChain,
  validFloat,
  isIllinoisProject,
  isIllinoisShinesProgram,
  max,
  nonEmptyBoolean,
  avoidedCostOfSolar
} from '~/schema/validators';
import { additionalLineItemSchema } from '../util';
import { Project } from '~/types/project';
import { sizeKwdc } from '~/utils/calculators';

const validation = {
  // Budget input ranges are no longer hard requirements (see issue #2036).
  epcCost: validFloat,
  epcCompanyName: nonEmptyString,
  epcCompanyType: nonEmptyString,
  siteLeaseAcreage: validFloat,
  deploymentType: nonEmptyString,
  developmentStage: nonEmptyString,
  partnerType: nonEmptyString,
  customerName: nonEmptyString,
  customerType: nonEmptyString,
  avoidedCostOfSolar: (value: any) => {
    if (value !== null) {
      return avoidedCostOfSolar(0.01, 0.50)(value);
    }
  },
  customerCreditRating: nonEmptyString,
  contractTerm: (value: any, project: Project) => project?.contractType !== ContractType.UNCONTRACTED && range(5, 40, ValidationRangeTypes.NUMBER)(value),
  contractRate: validFloat,
  contractEscalator: validFloat,
  communitySolarDiscountPercentage: validFloat,
  recOwnership: nonEmptyString,
  recsData: {
    'Illinois': {
      isShinesProgram: areRecStateFieldsEnabled('Illinois', nonEmptyString),
      recAllocation: isIllinoisShinesProgram(nonEmptyString),
      recProjectCategory: isIllinoisShinesProgram(nonEmptyString),
      recUtilityGroup: isIllinoisShinesProgram(nonEmptyString),
      recProgramYear: isIllinoisShinesProgram(nonEmptyString)
    },
    'New Jersey': {
      recAllocation: areRecStateFieldsEnabled('New Jersey', nonEmptyString),
      recValue: areRecStateFieldsEnabled('New Jersey', validationChain(validFloat, range(1, 500, ValidationRangeTypes.NUMBER))),
      recContractTerm: areRecStateFieldsEnabled('New Jersey', validFloat)
    }
  },
  name: nonEmptyString,
  addressCity: nonEmptyString,
  addressState: nonEmptyString,
  addressZip: nonEmptyString,
  addressStreet: nonEmptyString,
  expectedCommercialOperationDate: nonEmptyString,
  siteLeaseCost: validFloat,
  siteLeaseEscalator: validFloat,
  siteLeaseTerm: nonEmptyString,
  siteLeaseExtensions: validFloat,
  propertyTaxType: nonEmptyString,
  propertyTaxAmount: validFloat,
  propertyTaxEscalator: validFloat,
  customerPricingStatus: nonEmptyString,
  offtakeContractStatus: nonEmptyString,
  permitsStatus: nonEmptyString,
  siteControlStatus: nonEmptyString,
  interconnectionStatus: nonEmptyString,
  utilityZone: nonEmptyString,
  interconnectionType: nonEmptyString,
  pricingType: nonEmptyString,
  longTermOwner: nonEmptyString,
  revenueSource: nonEmptyString,
  // 'itcDetails.isItcEligible': nonEmptyBoolean,
  itcDetails: { isItcEligible: nonEmptyBoolean },
  projectArrays: [{
    type: nonEmptyString,
    sizeKwdc: validationChain(validFloat, range(0, 100000, ValidationRangeTypes.NUMBER)),
    sizeKwac: validationChain(isIllinoisProject(validFloat), range(0, 100000, ValidationRangeTypes.NUMBER)),
    roofCondition: (value: number, project: Project, index: number) => {
      if (project?.projectArrays?.[index]?.type === ProjectArrays.ROOFTOP) {
        return nonEmptyString(value);
      }
      return false;
    },
    yearOneProduction: (value: number, project: Project, index: number) => {
      let arraySize = 0;
      if (project?.projectArrays && project?.projectArrays.length > 0) {
        arraySize = sizeKwdc(project.projectArrays[index]);
      }
      const unit = project?.projectArrays?.[index]?.yearOneProductionUnit;
      if (unit === ProductionUnit.KWHKW) {
        return range(500, 3000, ValidationRangeTypes.NUMBER)(value);
      } else {
        let min = 500 * arraySize;
        let max = 3000 * arraySize;
        if (value < min || value > max) {
          return 'Outside expected range - please confirm unit selection';
        }
      }
    },
    moduleTiltDegrees: range(0, 90, ValidationRangeTypes.NUMBER),
    azimuthDegrees: range(0, 359, ValidationRangeTypes.NUMBER),
    derateFactor: max(20),
  }],
  storage: [{
    make: nonEmptyString,
    model: nonEmptyString,
    wattage: validFloat,
    discharge: validFloat
  }],
  additionalRevenues: [additionalLineItemSchema],
  additionalOperatingExpenses: [additionalLineItemSchema],
  subscriptionExpenseAmount: validFloat,
  subscriptionExpenseEscalator: validFloat,
  subscriptionExpenseExpectedTerm: validFloat,
  storageReplacementOmFlag: (value: any, project: Project) => project?.hasStorage && nonEmptyBoolean(value),
  storageExpenseAmount: validFloat,
  storageExpenseEscalator: validFloat,
  storageExpenseExpectedTerm: nonEmptyString,
  storageExpenseNotes: nonEmptyString
};

export default validation;
